import { ref, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { MultilanguageValue } from '@/core/utils/languages';
import { isObject } from '@/core/utils/type-guards';
import {
  ChannelManagerCode,
  DetermineAgeMethod,
  type GetTravelLinePlacingVariantsQuery,
  TravelLineMasterStep,
  type TravelLineSaveRoomCategoriesMappingMutationVariables,
  type TravelLineSaveServiceGroupsMappingMutationVariables,
  type TravelLineUpdatePlacingVariantsMutationVariables,
  type TravelLineUpdateRoomCategoriesMutationVariables,
  type TravelLineUpdateServiceGroupsMutationVariables,
  useGetTravelLinePlacingVariantsQuery,
  useGetTravelLineRestartIntegrationMutation,
  useTravelLineExternalHotelSuggestionsQuery,
  useTravelLineIntegrationQuery,
  useTravelLineRefreshTouristTypesMutation,
  useTravelLineSaveRoomCategoriesMappingMutation,
  useTravelLineSaveServiceGroupsMappingMutation,
  useTravelLineStartIntegrationMutation,
  useTravelLineUpdateIntegrationSettingsMutation,
  useTravelLineUpdatePlacingVariantsMutation,
  useTravelLineUpdateRoomCategoriesMutation,
  useTravelLineUpdateServiceGroupsMutation,
  useTravelLineUpdateTouristTypesMutation,
  useUncompletedIntegrationInfoQuery,
} from '@/graphql/generated';
import { errorTypes } from '@/parts/errors/utils/error-types';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import {
  ChannelManagerCustomError,
  type TLIntegrationData,
  type TLIntegrationHotelData,
} from '@/parts/tariffs/types/travel-line';

const hotelSuggestions = ref<TLIntegrationHotelData[]>();
const channelManagerCode = ref<ChannelManagerCode>();
const integration = ref<TLIntegrationData | null | undefined>();
const hasExternalIntegrationIssue = ref(false);
const externalIntegration = ref<{
  channelManagerCode: ChannelManagerCode;
  externalIntegration: {
    externalId?: string;
    integrationUrl?: string;
    externalHotel?: { hotelId: string; hotelName: string };
  };
  integrationId: string;
} | null>();
const integrationError = ref<ChannelManagerCustomError>();
const placingVariants = ref<GetTravelLinePlacingVariantsQuery['travelLinePlacingVariants']>();
const activeStep = ref(0);
const completedSteps = ref(0);

export const useTravelLineStore = (isEdit?: boolean) => {
  const $router = useRouter();
  const $route = useRoute();
  const { activeAccount } = useAccommodationObjectsStore();

  const contractNumber = toRef(() => activeAccount.value?.contractNumber);

  const interactionIdQuery = toRef(() => $route.query.interactionId);

  const setChannelManagerCode = (code: ChannelManagerCode | undefined) => {
    channelManagerCode.value = code;
  };

  const getTravelLineIntegration = async () => {
    if (!interactionIdQuery.value) {
      return;
    }

    let result;

    if (isEdit) {
      const { request, data } = useGetTravelLineRestartIntegrationMutation({
        interactionId: interactionIdQuery.value,
        contractNumber: contractNumber.value,
      });

      await request();

      if (data.value) {
        integration.value = data.value.travelLineRestartIntegration.View
        ? {
          channelManagerCode: data.value.travelLineRestartIntegration.View.channelManagerCode,
          master: data.value.travelLineRestartIntegration.View.master,
          externalIntegration: {
          },
        }
        : null;
        hasExternalIntegrationIssue.value =
          data.value.travelLineRestartIntegration.View?.hasExternalIntegrationIssue ?? false;
      }
    } else {
      const { request, data } = useTravelLineIntegrationQuery({
        contractNumber: contractNumber.value,
        interactionId: interactionIdQuery.value,
      });

      await request();

      if (data.value) {
        integration.value = data.value.travelLineIntegration
        ? {
          channelManagerCode: data.value.travelLineIntegration.channelManagerCode,
          master: data.value.travelLineIntegration.master,
          externalIntegration: {
            externalId: data.value.travelLineIntegration.externalIntegration?.externalId,
            integrationUrl: data.value.travelLineIntegration.externalIntegration?.integrationUrl ?? undefined,
            externalHotel: data.value.travelLineIntegration.externalIntegration?.externalHotel ?? undefined,
          },
        }
        : null;
        hasExternalIntegrationIssue.value = data.value.travelLineIntegration?.hasExternalIntegrationIssue ?? false;
      }
    }

    if (integration.value) {
      setChannelManagerCode(integration.value.channelManagerCode);
      externalIntegration.value = {
        channelManagerCode: integration.value.channelManagerCode,
        externalIntegration: {
          externalHotel: integration.value.externalIntegration?.externalHotel ?? externalIntegration.value?.externalIntegration?.externalHotel,
          externalId: integration.value.externalIntegration?.externalId ?? externalIntegration.value?.externalIntegration?.externalId,
          integrationUrl: integration.value.externalIntegration?.integrationUrl ?? externalIntegration.value?.externalIntegration?.integrationUrl
        },
        integrationId: String(interactionIdQuery.value),
      };
    }

    return result;
  };

  const setActiveStep = (step: number, onlyActive = false) => {
    activeStep.value = step;

    if (!onlyActive) {
      completedSteps.value = step - 1;
    }
  };

  const checkActiveStep = () => {
    if (integration.value) {
      const masterStep = integration.value.master?.masterStep;

      if (masterStep === TravelLineMasterStep.IntegrationSettings) {
        setActiveStep(3);
      }

      if (masterStep === TravelLineMasterStep.RoomCategoriesMapping) {
        setActiveStep(4);
      }

      if (masterStep === TravelLineMasterStep.ServiceGroupsMapping) {
        setActiveStep(5);
      }

      if (masterStep === TravelLineMasterStep.TouristTypesMapping) {
        setActiveStep(6);
      }

      if (masterStep === TravelLineMasterStep.PlacingVariantsMapping) {
        setActiveStep(7);
      }
    }
  };

  const updateIntegrationInfo = async () => {
    await getTravelLineIntegration();
    checkActiveStep();
  };

  const getHotelSuggestions = async () => {
    const { request, data } = useTravelLineExternalHotelSuggestionsQuery({
      contractNumber: contractNumber.value,
    });

    await request();

    if (data.value) {
      hotelSuggestions.value = data.value.travelLineExternalHotelSuggestions
        .filter((s) => s.channelManagerCode === channelManagerCode.value)
        .map((suggestion) => ({
          id: suggestion.externalHotelQuery.hotelId,
          name: suggestion.externalHotelQuery.hotelName,
          uncompletedInteractionId: suggestion.uncompletedInteractionId,
        }));
    }
  };

  const processUpdateIntegrationResponse = async (response?: { data?: unknown | null; errors?: unknown[] | null }) => {
    if (!response) {
      return;
    }

    if (!response.errors) {
      await updateIntegrationInfo();
    } else {
      const firstError = response.errors[0];

      if (isObject(firstError) && '__typename' in firstError) {
        if (firstError.__typename === errorTypes.TravelLineExternalIntegrationIssueError) {
          hasExternalIntegrationIssue.value = true;
        }

        if (firstError.__typename === errorTypes.ChannelManagerAdditionalMappingRequiredError) {
          integrationError.value = ChannelManagerCustomError.ChannelManagerAdditionalMappingRequiredError;

          await updateIntegrationInfo();
        }

        if (
          firstError.__typename === errorTypes.TravelLineTouristTypesHasGapsError &&
          integration.value?.master?.steps.touristTypesMapping
        ) {
          integration.value.master.steps.touristTypesMapping.touristTypesRefreshRequired = true;

          await updateIntegrationInfo();
        }

        if (firstError.__typename === errorTypes.TravelLineFulfillmentErrorError) {
          await updateIntegrationInfo();
        }
      }
    }
  };

  const checkUncompleteIntegrationInfo = async (hotelId: number, hotelName: string) => {
    if (!channelManagerCode.value) {
      return;
    }

    const uncompletedIntegrationInfoQuery = useUncompletedIntegrationInfoQuery({
      hotelId,
      hotelName,
      contractNumber: contractNumber.value,
      channelManagerCode: channelManagerCode.value,
    });

    await uncompletedIntegrationInfoQuery.request();

    const uncompletedIntegrationInfo = uncompletedIntegrationInfoQuery.data.value?.uncompletedIntegrationInfo;
    if (uncompletedIntegrationInfo) {
      externalIntegration.value = {
        channelManagerCode: uncompletedIntegrationInfo.channelManagerCode,
        externalIntegration: {
          externalId: uncompletedIntegrationInfo.externalIntegration.externalId,
          integrationUrl: uncompletedIntegrationInfo.externalIntegration.integrationUrl ?? undefined,
          externalHotel: uncompletedIntegrationInfo.externalIntegration.externalHotel ?? undefined
        },
        integrationId: uncompletedIntegrationInfo.integrationId,
      };
    }
  };

  const startIntegration = async (hotelId: number, hotelName: string) => {
    if (!channelManagerCode.value) {
      return;
    }

    const { request, response } = useTravelLineStartIntegrationMutation({
      hotelId,
      hotelName,
      contractNumber: contractNumber.value,
      channelManagerCode: channelManagerCode.value,
    });

    await request();

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const updateIntegrationSettings = async (payload: {
    connectionName: MultilanguageValue[];
    penaltySchemeCode: string;
    supplierPayRuleCode: string;
    checkInOutSettings: {
      checkInTime: string;
      checkOutTime: string;
      nextDayCheckout: boolean;
    };
  }) => {
    if (!integration.value || !interactionIdQuery.value) {
      return;
    }

    const { request, data, response } = useTravelLineUpdateIntegrationSettingsMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      connectionName: payload.connectionName,
      penaltySchemeCode: payload.penaltySchemeCode,
      supplierPayRuleCode: payload.supplierPayRuleCode,
      checkInTime: payload.checkInOutSettings.checkInTime,
      checkOutTime: payload.checkInOutSettings.checkOutTime,
      nextDayCheckout: payload.checkInOutSettings.nextDayCheckout,
    });

    await request();

    if (data.value && integration.value && integration.value.master) {
      integration.value.master.steps = data.value.travelLineUpdateIntegrationSettings.View!;
    }

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const saveRoomCategoriesMapping = async (
    payload: TravelLineSaveRoomCategoriesMappingMutationVariables['roomCategories'],
  ) => {
    const { request, response } = useTravelLineSaveRoomCategoriesMappingMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      roomCategories: payload,
    });

    await request();

    return response.value;
  };

  const saveServiceGroupsMapping = async (
    payload: TravelLineSaveServiceGroupsMappingMutationVariables['serviceGroups'],
  ) => {
    const { request, response } = useTravelLineSaveServiceGroupsMappingMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      serviceGroups: payload,
    });

    await request();

    return response.value;
  };

  const updateRoomCategories = async (payload: TravelLineUpdateRoomCategoriesMutationVariables['roomCategories']) => {
    const { request, data, response } = useTravelLineUpdateRoomCategoriesMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      roomCategories: payload,
    });

    await request();

    if (data.value && integration.value && integration.value.master) {
      integration.value.master.steps = data.value.travelLineUpdateRoomCategories.View!;
    }

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const updateTouristTypes = async (payload: { determineAgeMethod: DetermineAgeMethod }) => {
    const { request, data } = useTravelLineUpdateTouristTypesMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      determineAgeMethod: payload.determineAgeMethod,
    });

    const response = await request();

    if (data.value && integration.value && integration.value.master) {
      integration.value.master.steps = data.value.travelLineUpdateTouristTypes.View!;
    }

    if (response) {
      await processUpdateIntegrationResponse(response);
    }

    return response;
  };

  const getPlacingVariants = async () => {
    const { request, data, response } = useGetTravelLinePlacingVariantsQuery({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
    });

    await request();

    if (data.value) {
      placingVariants.value = data.value.travelLinePlacingVariants;
    }

    return response.value;
  };

  const updatePlacingVariants = async (
    payload: TravelLineUpdatePlacingVariantsMutationVariables['roomPlacingVariants'],
  ) => {
    const { request, response } = useTravelLineUpdatePlacingVariantsMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      roomPlacingVariants: payload,
    });

    await request();

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const updateServiceGroups = async (payload: TravelLineUpdateServiceGroupsMutationVariables['serviceGroups']) => {
    const { request, data, response } = useTravelLineUpdateServiceGroupsMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
      serviceGroups: payload,
    });

    await request();

    if (data.value && integration.value && integration.value.master) {
      integration.value.master.steps = data.value.travelLineUpdateServiceGroups.View!;
    }

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const refreshTouristTypes = async () => {
    const { request, response } = useTravelLineRefreshTouristTypesMutation({
      interactionId: interactionIdQuery.value,
      contractNumber: contractNumber.value,
    });

    await request();

    if (response.value) {
      await processUpdateIntegrationResponse(response.value);
    }

    return response.value;
  };

  const clearIntegration = () => {
    $router.replace({
      query: {
        interactionId: undefined,
      },
    });

    integration.value = undefined;
    placingVariants.value = undefined;
    activeStep.value = 0;
    completedSteps.value = 0;
    setChannelManagerCode(undefined);
    hasExternalIntegrationIssue.value = false;
    externalIntegration.value = undefined;
  };

  return {
    hotelSuggestions,
    channelManagerCode,
    integration,
    interactionIdQuery,
    activeStep,
    completedSteps,
    placingVariants,
    integrationError,
    hasExternalIntegrationIssue,
    externalIntegration,

    setChannelManagerCode,
    getHotelSuggestions,
    startIntegration,
    getTravelLineIntegration,
    updateIntegrationSettings,
    saveRoomCategoriesMapping,
    saveServiceGroupsMapping,
    updateRoomCategories,
    updateTouristTypes,
    checkActiveStep,
    setActiveStep,
    getPlacingVariants,
    updatePlacingVariants,
    updateServiceGroups,
    refreshTouristTypes,
    checkUncompleteIntegrationInfo,

    clearIntegration,
  };
};
